// 7-day volume history dropdown (SPEC.md §3), ported from
// legacy/dashboard.html's `volume-history-section`/`toggleHistory`/
// `fetchVolumeHistory`/`updateVolumeHistoryDisplay` now that btclock-api
// has a real `/v1/volume-history` endpoint (this task closed that gap --
// see volumeHistoryNotice.ts's former docstring for the backend-gap
// history this replaces). Structure/behavior matches legacy exactly
// (collapsible dropdown, date/USD/BTC/day-over-day-% columns, 5-minute
// auto-refresh); markup is built via `el()` only, per util/dom.ts's hard
// no-innerHTML rule.

import { el, clear } from "../util/dom";
import { calculatePercentChange, formatBtc, formatDayUtc, formatVolumeUsd } from "../util/format";
import { getVolumeHistory } from "../api/endpoints";
import type { DailyVolumeSnapshot } from "../api/types";

export interface VolumeHistoryView {
  root: HTMLElement;
  refresh(): Promise<void>;
  /** Day-over-day % change of the most recent day vs. the prior one, for
   * the `#volumeChange` badge next to the main 24H VOL stat -- null if
   * fewer than 2 real days of history exist yet. */
  latestDayOverDayChange(): number | null;
}

export function buildVolumeHistoryView(onError: (msg: string) => void): VolumeHistoryView {
  let snapshots: DailyVolumeSnapshot[] = [];

  const listEl = el("div", { class: "volume-dropdown", id: "volumeHistory" }, [
    el("div", { class: "loading" }, ["Loading history..."]),
  ]);

  const btn = el("button", { class: "expand-btn" }, ["▶ Show 7-Day Volume History"]);
  btn.addEventListener("click", () => {
    const expanded = listEl.classList.toggle("expanded");
    btn.textContent = expanded ? "▼ Hide 7-Day Volume History" : "▶ Show 7-Day Volume History";
  });

  const root = el("div", { class: "volume-history-section" }, [btn, listEl]);

  function render(): void {
    clear(listEl);
    if (snapshots.length === 0) {
      listEl.append(el("div", { class: "loading" }, ["No history available"]));
      return;
    }
    for (let index = 0; index < snapshots.length; index++) {
      const item = snapshots[index];
      // Compares to the NEXT (older) entry in the DESC-ordered list --
      // exactly legacy's `calculatePercentChange(item.volume_usd,
      // volumeHistoryData[index + 1].volume_usd)`.
      const older = index < snapshots.length - 1 ? snapshots[index + 1] : null;
      const pct = older ? calculatePercentChange(item.volume_usd, older.volume_usd) : null;
      const changeClass = pct === null ? "" : pct > 0 ? "positive" : pct < 0 ? "negative" : "";
      const changeText = pct === null ? "-" : `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`;

      listEl.append(
        el("div", { class: "volume-history-item" }, [
          el("span", { class: "date" }, [formatDayUtc(item.day_utc)]),
          el("span", { class: "amount" }, [formatVolumeUsd(item.volume_usd)]),
          el("span", { class: "btc-amount" }, [formatBtc(item.volume_btc)]),
          el("span", { class: `change ${changeClass}` }, [changeText]),
        ])
      );
    }
  }

  async function refresh(): Promise<void> {
    try {
      const resp = await getVolumeHistory(7);
      snapshots = resp.snapshots;
      render();
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    }
  }

  return {
    root,
    refresh,
    latestDayOverDayChange(): number | null {
      if (snapshots.length < 2) return null;
      return calculatePercentChange(snapshots[0].volume_usd, snapshots[1].volume_usd);
    },
  };
}
